import { trackEvent } from '@cx-web/utils/dataLayer';

import { Info } from '../../primitives/icon/icons';

import styles from './QuickLinks.module.css';
import { events, type QuickLinksProps } from './types';

// Approved values of the Contentful "Icon" dropdown; an unrecognised value renders no icon. Named
// imports rather than the `Icon` component, whose `icons[name]` lookup pulls the whole registry into
// the bundle — this card hydrates as an atlas-astro island.
const ICONS: Record<string, React.FC<React.SVGProps<SVGSVGElement>>> = {
  Information: Info,
};

const SCHEME_OR_PROTOCOL_RELATIVE = /^([a-z][a-z0-9+.-]*:|\/\/)/i;

// Contentful's Link URL is free text, so an EXTERNAL link is routinely authored without a scheme
// ("www.google.com") and the browser would resolve it against the current page. INTERNAL links are
// site paths and must stay relative.
const resolveHref = (linkUrl: string, isExternal: boolean): string =>
  isExternal && !SCHEME_OR_PROTOCOL_RELATIVE.test(linkUrl) ? `https://${linkUrl}` : linkUrl;

export const QuickLinks = ({ quickLinksItems, title }: QuickLinksProps): React.JSX.Element | null => {
  if (!quickLinksItems?.length) {
    return null;
  }

  const resolvedTitle = title?.trim();

  return (
    <div className={styles.container} data-testid="quick-links-container">
      {!!resolvedTitle && (
        <h2 className={styles.title} data-testid="quick-links-title">
          {resolvedTitle}
        </h2>
      )}
      <ul className={styles.list}>
        {quickLinksItems.map(({ icon, linkText, linkType, linkUrl }, index) => {
          // `Object.hasOwn` so an authored value like "constructor" cannot resolve to an inherited
          // `Object.prototype` member.
          const IconComponent = icon && Object.hasOwn(ICONS, icon) ? ICONS[icon] : undefined;
          const isExternal = linkType === 'EXTERNAL';
          const href = resolveHref(linkUrl, isExternal);

          return (
            <li className={styles.row} data-testid="quick-links-row" key={`${linkUrl}-${index}`}>
              <a
                className={styles.link}
                data-testid="quick-links-link"
                href={href}
                onClick={() => {
                  // GTM's built-in Page URL variable supplies AC3's "page clicked on". Navigation is
                  // not delayed behind a callback, so on INTERNAL links delivery relies on the GTM tag
                  // using an unload-safe transport (`beacon`).
                  trackEvent(events.MORE_LINKS_CLICK, {
                    clickUrl: href,
                    selection: linkText,
                  });
                }}
                {...(isExternal ? { rel: 'noopener noreferrer', target: '_blank' } : {})}
              >
                {!!IconComponent && (
                  <span aria-hidden="true" className={styles.icon} data-testid="quick-links-icon">
                    <IconComponent height={20} width={20} />
                  </span>
                )}
                <span className={styles.linkText}>{linkText}</span>
              </a>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default QuickLinks;
